import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { FcGoogle } from 'react-icons/fc';
import posthog from 'posthog-js';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';

import app from '../utils/firebaseApp.js';
import { AuthContext } from '../utils/auth.js';
import { isIPBlocked, getClientIP, storeRegistrationInfo } from '../utils/ipBlocker.js';
import { consumeIntendedPath, authErrorMessage } from '../utils/authRedirect.js';
import AuthLayout, { AuthDivider } from '../components/layout/AuthLayout.js';
import { Button, Field, Input, Checkbox, Callout } from '../ui/index.js';
import useDocumentTitle from '../hooks/useDocumentTitle.js';

function normalizeEmail(email) {
  if (!email) return email;
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  return `${localPart.split('+')[0]}@${domain}`;
}

function passwordStrength(value = '') {
  let score = 0;
  if (value.length >= 6) score += 1;
  if (value.length >= 10) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
  if (/\d/.test(value) || /[^A-Za-z0-9]/.test(value)) score += 1;
  return score; // 0..4
}

function Register() {
  useDocumentTitle('Create account');
  const history = useHistory();
  const { currentUser } = useContext(AuthContext);

  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [clientIP, setClientIP] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    getClientIP().then(setClientIP);
  }, []);

  useEffect(() => {
    if (currentUser && !submittingRef.current) {
      history.replace(consumeIntendedPath());
    }
  }, [currentUser, history]);

  const blocked = () => {
    if (clientIP && isIPBlocked(clientIP)) {
      setError('Registration is not available from your network.');
      return true;
    }
    return false;
  };

  const track = async (user, type) => {
    try {
      await storeRegistrationInfo({ type: 'register', registrationType: type, idToken: await user.getIdToken(), clientIP });
    } catch (e) {
      // non blocking
    }
    try {
      posthog.identify(user.uid, { email: user.email, userId: user.uid, clientIp: clientIP });
      posthog.capture('register', { type, email: user.email, clientIp: clientIP });
      window.dataLayer?.push({ event: 'register', type });
    } catch (e) {
      // analytics is best effort
    }
  };

  const signInWithGoogle = async () => {
    if (blocked()) return;
    setError('');
    setGoogleLoading(true);
    submittingRef.current = true;
    try {
      const result = await signInWithPopup(getAuth(app), new GoogleAuthProvider());
      await track(result.user, 'google');
      history.replace(consumeIntendedPath());
    } catch (err) {
      setError(authErrorMessage(err, 'Google sign-up failed. Please try again.'));
      submittingRef.current = false;
      setGoogleLoading(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    if (blocked()) return;
    const { email, confirm_password } = event.target.elements;

    if (!terms) {
      setError('Please accept the Terms of Use and Privacy Policy to continue.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm_password.value) {
      setError('The passwords do not match.');
      return;
    }

    setError('');
    setIsRegistering(true);
    submittingRef.current = true;
    try {
      const auth = getAuth(app);
      const credential = await createUserWithEmailAndPassword(auth, normalizeEmail(email.value.trim()), password);
      const user = credential.user;
      try {
        await sendEmailVerification(user);
      } catch (e) {
        // the verify screen offers a resend button
      }
      await track(user, 'email');
      // Email accounts must verify first; the route guard sends them there.
      history.replace('/verify-email');
    } catch (err) {
      setError(authErrorMessage(err));
      submittingRef.current = false;
      setIsRegistering(false);
    }
  };

  const strength = passwordStrength(password);
  const strengthLabel = ['Too short', 'Weak', 'Okay', 'Good', 'Strong'][strength];
  const strengthTone = ['bg-gray-200', 'bg-red-400', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-500'][strength];

  return (
    <AuthLayout
      title="Create your account"
      description="Start extracting data from documents in minutes."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-500">
            Sign in
          </Link>
        </>
      }
    >
      {error && (
        <Callout tone="danger" compact className="mb-5" onDismiss={() => setError('')}>
          {error}
        </Callout>
      )}

      <Button type="button" variant="secondary" size="lg" block onClick={signInWithGoogle} loading={googleLoading} disabled={isRegistering}>
        {!googleLoading && <FcGoogle size={18} aria-hidden="true" />}
        Continue with Google
      </Button>

      <AuthDivider>or register with email</AuthDivider>

      <form className="space-y-5" onSubmit={handleRegister} noValidate>
        <Field label="Email address" htmlFor="email">
          <Input id="email" name="email" type="email" autoComplete="email" required placeholder="you@company.com" size="lg" />
        </Field>

        <Field label="Password" htmlFor="password" hint={password ? undefined : 'At least 6 characters.'}>
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            size="lg"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            trailing={
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="rounded-md p-1 text-gray-400 hover:text-gray-600" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            }
          />
          {password && (
            <div className="mt-2 flex items-center gap-2" aria-live="polite">
              <div className="flex flex-1 gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <span key={i} className={`h-1 flex-1 rounded-full ${i <= strength ? strengthTone : 'bg-gray-200'}`} />
                ))}
              </div>
              <span className="text-xs text-gray-500">{strengthLabel}</span>
            </div>
          )}
        </Field>

        <Field label="Confirm password" htmlFor="confirm_password">
          <Input id="confirm_password" name="confirm_password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required size="lg" />
        </Field>

        <Checkbox
          id="terms"
          name="terms"
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          label={
            <span className="font-normal text-gray-700">
              I agree to the{' '}
              <a href="https://extracta.ai/terms-and-conditions/" className="font-medium text-indigo-600 hover:text-indigo-500" target="_blank" rel="noreferrer">
                Terms of Use
              </a>{' '}
              and{' '}
              <a href="https://extracta.ai/privacy-policy/" className="font-medium text-indigo-600 hover:text-indigo-500" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </span>
          }
        />

        <Button type="submit" size="lg" block loading={isRegistering} disabled={googleLoading}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export default Register;
